import { Directive, ElementRef, HostListener, Input, EventEmitter, AfterViewInit, PLATFORM_ID, Inject, Renderer2, SimpleChanges, Optional } from '@angular/core';
import { PhotoSwipOptions } from './entity/PhotoSwipOptions';
import { PhotoSwipeService } from './photo-swipe.service';
import { PhotoSwipeFigureSlide } from './entity/PhotoSwipeSlide';
import { timer } from 'rxjs';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { loadLazyCssFile, loadLazyJsFile } from '@techmore/global';
import { PHOTO_SWIPE_ASSETS, photoSwipeAssetsDefaultConfig } from './assets.token';

/**
 * 可手勢滑動的相簿瀏覽器
 * https://photoswipe.com/documentation/getting-started.html
 */
@Directive({
  selector: '[tmPhotoSwipe]'
})
export class PhotoSwipeDirective implements AfterViewInit {
  /** photoswipe 插件初始化完成後觸發 */
  static pulginInit = new EventEmitter<void>();
  /** 全域的 photoswipe 插件初始化完成 */
  static isPluginInit = false;
  /** 全域的 photoswipe 插件在正初始化中 */
  static isPluginIniting = false;
  /** 載入插件的方法 */
  static loadPlugin(document: Document, renderer: Renderer2, parentEl: HTMLElement, isBrowser: boolean, assetsConfig: { scripts: string[]; styles: string[]; }) {
    PhotoSwipeDirective.isPluginIniting = true;
    assetsConfig.scripts.forEach(sc => {
      const sca = sc.split('/');
      const scName = sca[sca.length - 1];
      loadLazyJsFile(document, renderer, scName, sc);
    });
    assetsConfig.styles.forEach(st => {
      const sta = st.split('/');
      const stName = sta[sta.length - 1];
      loadLazyCssFile(document, renderer, parentEl, stName, st);
    });
    checkPluginCanUse(() => {
      PhotoSwipeDirective.isPluginInit = true;
      PhotoSwipeDirective.isPluginIniting = false;
      PhotoSwipeDirective.pulginInit.emit();
      PhotoSwipeDirective.pulginInit.complete();
    });
  }
  /** 是否渲染端為瀏覽器 */
  protected isBrowser: boolean;
  /** 可以開始使用元件了 */
  canInit = false;
  // tslint:disable-next-line: no-input-rename
  @Input('tmPhotoSwipe') potoswipeOptions?: PhotoSwipOptions;
  @Input() potoswipeSlides?: PhotoSwipeFigureSlide[];
  /** 用來剖析出相片物件陣列的元素，預設是宿主元素 */
  @Input() slideListEl?: HTMLElement;
  /** 是否藉由 figure 的 attr.data-item 去決定 對應的 PhotoSwipeFigureSlide, 預設否 (用 owl輪播的loop, 需開啟) */
  @Input() mapCurrentItemByDataItem = false;

  constructor(
    protected el: ElementRef,
    @Inject(PLATFORM_ID) protected platformId: any,
    protected render: Renderer2,
    @Inject(DOCUMENT) protected document: any,
    @Optional() @Inject(PHOTO_SWIPE_ASSETS) protected assets: any,
    protected photoSwipeService: PhotoSwipeService,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngAfterViewInit() {
    if (this.isBrowser) {
      if (!PhotoSwipeDirective.isPluginInit && !PhotoSwipeDirective.isPluginIniting) {
        if (isPluginLoaded()) {
          PhotoSwipeDirective.isPluginInit = true;
        } else {
          PhotoSwipeDirective.loadPlugin(this.document, this.render, this.el.nativeElement, this.isBrowser, this.assets || photoSwipeAssetsDefaultConfig);
          this.slideListEl = this.el.nativeElement;
        }
      }

      if (!PhotoSwipeDirective.isPluginInit) {
        PhotoSwipeDirective.pulginInit.subscribe(() => {
          this.canInit = true;
        });
      } else {
        this.canInit = true;
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('slideListEl' in changes) {
      this.slideListEl = changes['slideListEl'].currentValue;
    }
  }

  @HostListener('click', ['$event'])
  onThumbnailsClick(evt: any) {
    evt = evt || window.event;
    evt.preventDefault ? evt.preventDefault() : evt.returnValue = false;

    if (!this.canInit) {
      alert('相簿檢視器插件尚未完全載入，請稍後再試');
      return;
    }

    const eTarget = evt.target || evt.srcElement;
    let selectedIdx;
    const clickedListItem = this.closest(eTarget, (el: any) => {
      return (el.tagName && el.tagName.toUpperCase() === 'FIGURE');
    });

    if (!clickedListItem) { return; }
    selectedIdx = clickedListItem.dataset.item;

    if (selectedIdx >= 0) {
      
      // 針對比較多層圖片列表做調整
      let galleryEl: HTMLElement;
      if (this.potoswipeOptions?.isSecondChild) {
        galleryEl = (this.slideListEl as HTMLElement).children[0].children[0] as HTMLElement;
      } else {
        galleryEl = this.slideListEl as HTMLElement;
      }

      this.photoSwipeService.open(selectedIdx, this.potoswipeSlides as PhotoSwipeFigureSlide[], galleryEl, this.potoswipeOptions, this.mapCurrentItemByDataItem);
    }
  }

  close() {
    this.photoSwipeService.close();
  }

  protected closest(el: any, fn: any): any {
    return el && (fn(el) ? el : this.closest(el.parentNode, fn));
  }
}

function isPluginLoaded() {
  return (window as any).PhotoSwipe;
}

const checkPluginCanUse = (cb: () => void) => {
  const cpTmSubc = timer(0, 300).subscribe(t => {
    if (isPluginLoaded()) {
      cpTmSubc.unsubscribe();
      cb();
    }
  });
}
